import { AccessBehaviorProps } from "../AccessGate.types";
import {
    OverlayWrap,
    TooltipWrap,
} from "../../benefits/components/wrappers/BenefitWrapper";

/**
 * Config accepted by OverlayBehavior through `behaviorConfig`.
 *
 * - `tooltip`       – text shown on hover.
 * - `overlayLabel`  – label rendered inside the blur overlay.
 * - `overlay`       – whether to render the overlay layer (default: true).
 * - `action`        – optional ReactNode rendered inside the overlay
 *                     (e.g. an "Upgrade" button).
 * - `sx` / `overlaySx` – style overrides forwarded to wrappers.
 */
export type OverlayBehaviorConfig = {
    tooltip?: string;
    overlayLabel?: string;
    overlay?: boolean;
    action?: React.ReactNode;
    sx?: Record<string, unknown>;
    overlaySx?: Record<string, unknown>;
};

/**
 * Behavior that wraps children in a dimmed, blurred overlay when
 * access is blocked. Good for large sections (charts, panels).
 *
 * @example
 * <AccessGate
 *   strategy={access}
 *   behavior={OverlayBehavior}
 *   behaviorConfig={{ overlayLabel: "Premium", tooltip: "Upgrade to access" }}
 * >
 *   <AdvancedMetrics />
 * </AccessGate>
 */
export function OverlayBehavior({ children, config }: AccessBehaviorProps) {
    const {
        tooltip = "",
        overlayLabel = "",
        overlay = true,
        action,
        sx,
        overlaySx,
    } = (config ?? {}) as OverlayBehaviorConfig;

    const tooltipEnabled = !!tooltip;

    return (
        <TooltipWrap enabled={tooltipEnabled} title={tooltip} display="block" sx={sx}>
            <OverlayWrap overlay={overlay} label={overlayLabel} sx={sx} overlaySx={overlaySx} action={action}>
                {children}
            </OverlayWrap>
        </TooltipWrap>
    );
}
